<template>
  <div class="container">
    <DatePicker
      v-if="range && Object.keys(range).length"
      v-model.range="computedRange"
      :max-date="maxDate"
      :min-date="minDate"
      :initial-page="initialPage"
    >
      <template #default="{ inputValue, showPopover }">
        <PInput
          ref="pInput"
          v-bind="$attrs"
          :label="$attrs.label"
          :model-value="getRangeInputValue(inputValue)"
          :placeholder="$attrs.placeholder"
          :required="$attrs.required"
          class="prepend-bg"
          debounce="300"
          v-on="{}"
          @focus="showPopover()"
        >
          <template #prepend>
            <CalendarIcon class="cursor-pointer icon" />
          </template>
        </PInput>
      </template>
    </DatePicker>
    <DatePicker
      v-else
      :model-value="computedModelValue"
      :masks="masks"
      :max-date="maxDate"
      :min-date="minDate"
      :mode="type"
      :popover="false"
      :tabindex="disable ? -1 : 'initial'"
      :rules="dateTimeRules"
      color="primary"
      @update:model-value="onUpdateModelValue"
    >
      <template #default="{ inputEvents, inputValue, showPopover }">
        <PInput
          ref="pInput"
          v-bind="$attrs"
          :disable="disable"
          :label="$attrs.label"
          :model-value="getInputValue(inputValue)"
          :placeholder="$attrs.placeholder"
          :required="$attrs.required"
          class="prepend-bg"
          debounce="300"
          v-on="inputEvents"
          @focus="showPopover()"
        >
          <template #prepend>
            <CalendarIcon class="cursor-pointer icon" />
          </template>
        </PInput>
      </template>
    </DatePicker>
  </div>
</template>

<script setup>
import { date } from "quasar";
import CalendarIcon from "assets/icons/calendar.svg";

import { DatePicker } from "@angelblanco/v-calendar";
import "@angelblanco/v-calendar/dist/style.css";

const { formatDate } = date;

const masks = ref({
  input: "DD-MM-YYYY",
});

const now = new Date();

const props = defineProps({
  disable: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [Date, String, Object],
    default: null,
  },
  maxDate: {
    type: Date,
    default: null,
  },
  minDate: {
    type: Date,
    default: null,
  },
  type: {
    type: String,
    default: "date",
  },
  dateTimeRules: {
    type: Object,
    default: () => {
      return {};
    },
  },
  range: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const pInput = ref(null);

const emit = defineEmits(["update:modelValue"]);

const computedModelValue = computed(() => {
  return props.modelValue ? new Date(props.modelValue) : null;
});

const onUpdateModelValue = (value) => {
  value = props.type === "date" ? formatDate(value, "YYYY-MM-DD") : value;

  emit("update:modelValue", value);

  unref(unref(pInput).component).resetValidation();
};

const getInputValue = (inputValue) => {
  if (props.modelValue) {
    return inputValue;
  } else {
    return null;
  }
};

const computedRange = computed({
  get() {
    return props.range;
  },
  set(value) {
    if (value) {
      emit("update:modelValue", value);
    }
  },
});

const getRangeInputValue = (inputValue) => {
  return inputValue.start && inputValue.end
    ? inputValue.start + " - " + inputValue.end
    : "";
};

const initialPage = ref({
  year: Number(date.formatDate(now, "YYYY")),
  month: Number(date.formatDate(now, "M")),
});
</script>

<style scoped lang="scss">
.container {
  font-size: 0.875rem;
  line-height: 1.33;

  :deep(.on-left),
  :deep(.on-right) {
    margin: 0;
  }

  :deep(.vc-primary) {
    --vc-accent-600: var(--q-primary);
  }

  :deep(.vc-blue) {
    --vc-accent-200: #{$primary-40};
  }

  :deep(.vc-header .vc-arrow),
  :deep(.vc-header .vc-title),
  :deep(.vc-nav-arrow),
  :deep(.vc-nav-title) {
    color: black;
    background-color: white;
  }

  :deep(.vc-time-weekday),
  :deep(.vc-time-year) {
    color: var(--q-primary);
    background-color: white;
  }

  :deep(.vc-nav-item.is-active) {
    color: white;
    background-color: var(--q-primary);
  }

  :deep(.vc-highlight-content-outline),
  :deep(.vc-highlight-content-solid) {
    color: white;
    background-color: var(--q-primary);
  }

  :deep(.vc-highlight-content-light) {
    color: $primary-80;
  }

  :deep(.vc-nav-item) {
    color: black;
    background-color: white;
  }

  :deep(.vc-time-select-group) {
    color: black;
    background-color: white;
    border-color: white;
  }

  :deep(.vc-base-select) {
    margin-inline-start: 10px;
    margin-inline-end: 10px;
  }

  :deep(.vc-light) {
    --vc-focus-ring: 0 0 0 2px #bca394;
  }

  :deep(.vc-title) {
    &:focus {
      box-shadow: var(--vc-focus-ring);
    }
  }
}
</style>
